import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, FolderOpen } from "lucide-react";
import { CyberGrid } from "./CyberGrid";
import { Magnetic } from "./Magnetic";
import { CountUp } from "./CountUp";
import { WHATSAPP_LINK } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.25], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.22], [1, 0]);

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden">
      <CyberGrid />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full opacity-25 blur-[140px]"
        style={{ background: "radial-gradient(circle, #E50914 0%, transparent 65%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-52 bottom-0 h-[420px] w-[420px] rounded-full opacity-15 blur-[120px]"
        style={{ background: "radial-gradient(circle, #E50914 0%, transparent 65%)" }}
      />

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
          data-testid="hero-status-badge"
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-[#22222A] bg-[#101014]/80 px-4 py-2 backdrop-blur"
        >
          <span className="status-dot h-2 w-2 rounded-full bg-[#E50914]" />
          <span className="font-mono-code text-xs uppercase tracking-[0.2em] text-zinc-300">
            Disponível para novos projetos
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold leading-[1.02] tracking-tight sm:leading-[0.95]">
          <MaskedLine delay={0.45}>
            <span className="text-[10.5vw] text-zinc-100 sm:text-6xl lg:text-8xl">CRIANDO</span>
          </MaskedLine>
          <MaskedLine delay={0.58}>
            <span className="text-outline whitespace-nowrap text-[6vw] sm:text-4xl lg:text-5xl xl:text-6xl">
              EXPERIÊNCIAS WEB
            </span>
          </MaskedLine>
          <MaskedLine delay={0.71}>
            <span className="text-[11vw] text-zinc-100 sm:text-6xl lg:text-7xl">
              DE <span className="text-[#E50914]">ALTO IMPACTO</span>
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
        >
          Sou <span className="font-semibold text-zinc-100">Luiz Henrique</span>, desenvolvedor web.
          Construo sites profissionais para <span className="text-zinc-200">empresas</span> e
          plataformas imersivas para <span className="text-zinc-200">estúdios de jogos</span> —
          código limpo, velocidade extrema e design que converte.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Magnetic>
            <a
              data-testid="hero-cta-whatsapp"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-red-glow btn-shine flex items-center gap-2.5 rounded-full bg-[#E50914] px-8 py-4 font-mono-code text-sm font-bold uppercase tracking-widest text-white"
            >
              <MessageCircle className="h-4 w-4" />
              Falar no WhatsApp
            </a>
          </Magnetic>
          <Magnetic>
            <button
              data-testid="hero-cta-portfolio"
              onClick={() => scrollToSection("projetos")}
              className="flex items-center gap-2.5 rounded-full border border-[#2A2A33] bg-[#101014]/60 px-8 py-4 font-mono-code text-sm font-bold uppercase tracking-widest text-zinc-200 backdrop-blur transition-colors duration-300 hover:border-[#E50914]/60 hover:text-white"
            >
              <FolderOpen className="h-4 w-4 text-[#E50914]" />
              Ver Projetos
            </button>
          </Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 flex items-center gap-8 sm:gap-12"
        >
          {[
            ["20+", "Projetos entregues"],
            ["100%", "Clientes satisfeitos"],
            ["2", "Especialidades"],
          ].map(([num, label]) => (
            <div key={label} data-testid={`hero-stat-${label.split(" ")[0].toLowerCase()}`}>
              <CountUp value={num} className="block font-display text-2xl font-extrabold text-zinc-100 sm:text-3xl" />
              <div className="mt-1 font-mono-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 sm:text-xs">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToSection("sobre")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-label="Rolar para baixo"
      >
        <div className="flex h-12 w-7 items-start justify-center rounded-full border border-zinc-700 pt-2">
          <div className="scroll-hint-dot h-2 w-1 rounded-full bg-[#E50914]" />
        </div>
        <ArrowDown className="mx-auto mt-2 h-3.5 w-3.5 text-zinc-600" />
      </motion.button>

      <div
        aria-hidden="true"
        className="absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 rotate-90 xl:block"
      >
        <span className="font-mono-code text-[10px] uppercase tracking-[0.5em] text-zinc-600">
          LUIZ HENRIQUE — DESENVOLVEDOR WEB — 2026
        </span>
      </div>
    </section>
  );
};
